/**
 * 메일 본문 HTML 뷰어 — 외부 / 내부 메일의 raw HTML 을 안전하게 렌더.
 *
 * 왜 별도 컴포넌트인가
 *   메일 본문 표시는 보기엔 단순 "HTML 박기" 같지만 세 가지 처리가 동시에 필요해서
 *   MailDetail.jsx 한가운데에 두면 파일이 빠르게 읽기 어려워짐.
 *
 *     1) 보안 sanitize (DOMPurify) — XSS 방지. 외부에서 온 임의 HTML 신뢰 불가.
 *     2) URL rewrite — cid:/ 상대 파일명 → 백엔드 inline endpoint, 인증 토큰 부착.
 *     3) 본문 CSS 정규화 — 단락 간격, 표, 인용문 등 viewer 일관성.
 *
 *   한 곳에 묶어 두면 본문 처리 정책 변경 시 단일 파일만 만지면 됨. (인증 정책 / cid 패턴 / 표 정책)
 *
 * 라이브러리로 못 빼는 이유
 *   - 인증 토큰 부착은 우리 EmployeeTokenInterceptor 정책에 강결합
 *   - inline endpoint URL 경로 / contentId 정규화도 백엔드와 약속된 형태
 *   - 메일 viewer 라이브러리는 sanitize 정도만 다룸 (cid 처리 / 토큰 / 표 정규화는 모두 호출측 책임)
 *
 * 사용 예
 *   <MailBodyHtml html={mail.bodyHtml} mailId={mail.id} attachments={mail.attachments} />
 *
 * Fallback
 *   html 이 비어 있고 fallbackText 가 있으면 pre-wrap 으로 텍스트 렌더 (받은 메일이 text/plain 만인 경우).
 */
import React from "react";
import { Box, Typography } from "@mui/material";
import DOMPurify from "dompurify";
import { ds } from "../../../../styles/designSystem";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

/**
 * DOMPurify v3 default 설정은 보안 우선이라 메일 viewer 에 필요한 속성도 지움.
 * 우리 메일 본문은 발송 시 한 번 jsoup 으로 sanitize 된 신뢰 가능한 HTML 이라
 * 렌더에 필수인 속성·data:URI 만 명시적으로 허용.
 *
 *   - style/class       : 표 width / 셀 border 등 inline 스타일 보존
 *   - colspan/rowspan/colwidth/cellpadding/cellspacing/border/align/valign/bgcolor: 표 속성
 *   - data:URI on <img> : 클립보드 붙여넣기 base64 이미지 보존
 */
const PURIFY_OPTS = {
    ADD_ATTR: [
        "target", "rel", "style", "class",
        "colspan", "rowspan", "colwidth",
        "cellpadding", "cellspacing", "border",
        "width", "height",
        "align", "valign", "bgcolor",
    ],
    ADD_TAGS: ["colgroup", "col"],
    ADD_DATA_URI_TAGS: ["img"],
};

/**
 * 메일 본문 HTML 의 이미지 URL 을 viewer 가 다룰 수 있는 형태로 rewrite.
 *
 * 처리 케이스
 *   1) {@code <img src="cid:xxx">} (RFC 2392) — multipart/related 안에서만 의미.
 *      → {@code /api/mail/attachments/inline/{mailId}/{cid}?token=}
 *
 *   2) {@code <img src="filename.ext">} 가 inline 첨부 filename 과 일치 (Outlook 시그니처 패턴).
 *      Outlook / 일부 클라이언트가 cid: 대신 Content-Location 기반 상대 참조 사용.
 *      → 같은 inline endpoint 로 rewrite (백엔드가 contentId 매칭 실패 시 filename fallback).
 *
 *   3) {@code <img src="/api/mail/signatures/images/...">} — 사내 서명 이미지.
 *      X-TOKEN 헤더 인증 필요한데 browser native &lt;img&gt; 는 헤더 못 실음 → ?token= 쿼리 부착.
 */
export function rewriteImageUrls(rawHtml, mailId, attachments) {
    if (!rawHtml) return rawHtml;
    const token = localStorage.getItem("auth_token");
    const tokenParam = token ? encodeURIComponent(token) : null;
    const tokenSuffix = tokenParam ? `?token=${tokenParam}` : "";

    let result = rawHtml;

    // (1) cid: → inline endpoint
    result = result.replace(
        /<img([^>]*?)src=(["'])cid:([^"']+?)(\2)/gi,
        (_, attrs, q1, cid, q2) => {
            const url = `${API_BASE}/mail/attachments/inline/${mailId}/${encodeURIComponent(cid)}`;
            return `<img${attrs}src=${q1}${url}${tokenSuffix}${q2}`;
        }
    );

    // (2) <img src="filename.ext"> 가 inline 첨부와 매칭 → inline endpoint
    const inlineAtts = (attachments || []).filter(a => a.isInline && a.filename);
    if (inlineAtts.length > 0) {
        const byFilename = new Map(inlineAtts.map(a => [a.filename, a]));
        result = result.replace(
            /<img([^>]*?)src=(["'])([^"':\/?#]+\.[A-Za-z0-9]{2,5})(\2)/gi,
            (full, attrs, q1, filename, q2) => {
                if (!byFilename.has(filename)) return full;
                const url = `${API_BASE}/mail/attachments/inline/${mailId}/${encodeURIComponent(filename)}`;
                return `<img${attrs}src=${q1}${url}${tokenSuffix}${q2}`;
            }
        );
    }

    // (3) 토큰 인증 필요한 우리 백엔드 endpoint 에 host prefix + ?token= 부착.
    //     - /api/mail/signatures/images/... — 사내 서명 이미지
    //     - /api/mail/attachments/inline/...  — 인라인 이미지 (백엔드 MailQueryService 가 본문에 미리 박음)
    //
    //     [중요] host prefix (API_BASE) 가 필수.
    //     백엔드가 본문에 박을 때는 상대 path 만 박는데, dev 환경에서는 React dev server (localhost:3000) 가
    //     /api/... 호출을 받아 SPA fallback (index.html) 응답 → broken image.
    //     절대 URL 로 만들어야 백엔드 (예: localhost:11115) 직접 호출 → 정상 응답.
    //
    //     browser native <img> 는 X-TOKEN 헤더 못 실어 ?token= 쿼리 필수.
    //
    //     [절대 URL 도 잡아야 하는 이유 — 보낸메일함 인용 이미지가 깨지던 원인]
    //     전달/답장 폼에서 (1)(2) 가 만든 결과는 "https://host/api/..." 절대 URL 이고, 그 본문이
    //     그대로 S3 에 저장된다. 발송용 EML 은 서버가 cid 로 되돌리지만 저장본은 절대 URL 인 채로 남는다.
    //     그런데 예전 정규식은 "/api/..." 상대 경로만 매칭해서 저장본을 아예 못 잡았다.
    //     → 보낸메일함에서 그 메일을 열면 30분 전 토큰이 박힌 주소로 요청 → 401 → 깨진 이미지.
    //
    //     [기존 토큰을 덧붙이지 않고 버리는 이유]
    //     예전엔 `url.includes("?") ? "&" : "?"` 로 뒤에 붙였다. 그러면 ?token=만료된값&token=새값 이
    //     되는데, 서버(request.getParameter)는 <b>첫 번째</b> 값을 읽는다 → 만료된 토큰으로 401.
    //     그래서 기존 쿼리스트링은 통째로 버리고 현재 토큰만 새로 단다.
    if (tokenParam) {
        result = result.replace(
            /<img([^>]*?)src=(["'])(?:https?:\/\/[^/"'\s]+)?\/api\/mail\/(signatures\/images|attachments\/inline)\/([^"'\s?]+)(?:\?[^"']*)?(\2)/gi,
            (_, attrs, q1, kind, rest, q2) =>
                `<img${attrs}src=${q1}${API_BASE}/mail/${kind}/${rest}?token=${tokenParam}${q2}`
        );
    }
    return result;
}

/**
 * viewer 본문 CSS — 단락 / 표 / 이미지 / 인용문 등 viewer 일관성.
 *
 * 표 정책
 *   발신자 의도 보존이 원칙. 우리가 보낸 메일 (Tiptap) 은 발송 시점에 모든 td/th 에 inline border 가
 *   박혀 있어 viewer 추가 border 불필요. 외부 메일 (Adobe / Naver 등) 은 표를 레이아웃 그리드로
 *   쓰면서 일부러 border 없이 보내는 케이스가 많아 viewer 강제 border 는 격자무늬 부작용을 유발.
 *
 * 셀 word-break
 *   `overflow-wrap: break-word` 만 — 너무 긴 단어만 줄바꿈, 일반 단어는 그대로.
 *   (이전 `word-break: break-word + overflow-wrap: anywhere` 는 단어 한가운데에서도 잘라
 *    'www.onandme.com' 이 'www.onand'/'me.com' 으로 깨지는 부작용 있었음.)
 */
const BODY_SX = {
    fontSize: 14, color: "#1e293b", lineHeight: 1.55,
    "& > *:first-of-type": { mt: 0 },
    "& > *:last-of-type":  { mb: 0 },
    "& p":  { m: 0, mb: 0.4 },
    "& h1, & h2, & h3, & h4, & h5, & h6": { mt: 1.2, mb: 0.6 },
    "& ul, & ol": { mt: 0.4, mb: 0.4, pl: 3 },
    "& li":  { mt: 0, mb: 0.2 },
    "& br":  { lineHeight: 1.4 },
    "& a":   { color: ds.colors.primary },
    "& img": { maxWidth: "100%", height: "auto" },
    "& blockquote": {
        borderLeft: "3px solid #cbd5e1", pl: 2, ml: 0, my: 0.6,
        color: "#64748b",
    },
    "& table": { my: 0.6, maxWidth: "100%", tableLayout: "auto" },
    "& td, & th": { verticalAlign: "top", overflowWrap: "break-word" },
};

const FALLBACK_SX = {
    fontSize: 14, color: "#1e293b", lineHeight: 1.55,
    whiteSpace: "pre-wrap", fontFamily: "inherit", m: 0,
};

export default function MailBodyHtml({ html, mailId, attachments, fallbackText }) {
    /*
     * 표 잘림 방지 — wrapper 에 overflow-x:auto.
     * Tiptap 표는 셀에 inline colwidth 가 들어가 컨테이너 폭을 초과하는 경우 우측이 잘려 보임.
     * 가로 스크롤바로 사용자가 전체를 볼 수 있게 함 (Gmail / Outlook viewer 와 동일 패턴).
     */
    const safeHtml = React.useMemo(() => {
        if (!html) return null;
        // 진단 — sanitize/rewrite 가 큰 본문에서 병목인 경우 수십~수백ms 가 나올 수 있음.
        const t0 = performance.now();
        const rewritten = rewriteImageUrls(html, mailId, attachments);
        const t1 = performance.now();
        const sanitized = DOMPurify.sanitize(rewritten, PURIFY_OPTS);
        const t2 = performance.now();
        if (t2 - t0 > 5) {
            console.log(
                `[MailBodyHtml timing] mailId=${mailId} ` +
                `rewrite=${(t1 - t0).toFixed(1)}ms ` +
                `sanitize=${(t2 - t1).toFixed(1)}ms ` +
                `total=${(t2 - t0).toFixed(1)}ms inputLen=${html.length} outputLen=${sanitized.length}`
            );
        }
        return sanitized;
    }, [html, mailId, attachments]);

    return (
        <Box sx={{ mb: 2, overflowX: "auto" }}>
            {safeHtml ? (
                <Box sx={BODY_SX} dangerouslySetInnerHTML={{ __html: safeHtml }} />
            ) : (
                <Typography component="pre" sx={FALLBACK_SX}>
                    {fallbackText || "(본문 없음)"}
                </Typography>
            )}
        </Box>
    );
}
